<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 策略名称 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">策略名称</label>
      <input v-model="form.name" type="text" required
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如：默认推广策略" />
    </div>

    <!-- 用户分群 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">用户分群</label>
      <input v-model="form.segment" type="text" required
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如：normal" />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <!-- 最大层级 -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">最大层级</label>
        <input v-model.number="form.maxLevel" type="number" min="1" max="5" required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200" />
      </div>

      <!-- 触发模式 -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">触发模式</label>
        <select v-model="form.triggerMode" required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200">
          <option value="ALL_PAID">ALL_PAID（每次付费）</option>
          <option value="FIRST_PAID">FIRST_PAID（首次付费）</option>
        </select>
      </div>
    </div>

    <!-- 适用订单类型 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">适用订单类型</label>
      <div class="flex gap-4">
        <label v-for="ot in orderTypeOptions" :key="ot" class="flex items-center gap-1.5">
          <input type="checkbox" :value="ot" v-model="form.eligibleOrderTypes"
            class="h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" />
          <span class="text-sm text-gray-700 dark:text-gray-300">{{ ot }}</span>
        </label>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4">
      <!-- 是否启用 -->
      <div>
        <label class="flex items-center gap-2">
          <input type="checkbox" v-model="form.isActive"
            class="h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" />
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">启用</span>
        </label>
      </div>

      <!-- 生效时间 -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">生效时间（可选）</label>
        <input v-model="form.effectiveAt" type="datetime-local"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200" />
      </div>
    </div>

    <!-- 层级费率 -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">层级费率</label>
        <button type="button" @click="addLevel"
          class="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
          + 添加层级
        </button>
      </div>
      <div v-for="(lv, idx) in form.levels" :key="idx"
        class="flex items-center gap-3 mb-2">
        <div class="flex-1">
          <label class="text-xs text-gray-500 dark:text-gray-400">层级</label>
          <input v-model.number="lv.level" type="number" min="1" required
            class="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200" />
        </div>
        <div class="flex-1">
          <label class="text-xs text-gray-500 dark:text-gray-400">费率（%）</label>
          <input v-model.number="lv.ratePercent" type="number" min="0" max="100" step="0.1" required
            class="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200" />
        </div>
        <button type="button" @click="removeLevel(idx)"
          class="mt-4 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm">
          删除
        </button>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
      <button type="button" @click="$emit('cancel')"
        class="cancel-btn">
        取消
      </button>
      <button type="submit"
        class="primary-btn">
        {{ isEdit ? '更新' : '创建' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue'
import type { CommissionPolicy, PolicyCreateRequest } from '@/types/referral/policy'

const props = defineProps<{
  policy?: CommissionPolicy
  isEdit: boolean
}>()

const emit = defineEmits<{
  (e: 'submit', data: PolicyCreateRequest): void
  (e: 'cancel'): void
}>()

const orderTypeOptions = ['NEW', 'RENEW', 'UPGRADE']

/** 表单内部用 ratePercent 方便输入，提交时转为小数 */
interface LevelRow {
  level: number
  ratePercent: number
}

const form = reactive({
  name: '',
  segment: 'normal',
  maxLevel: 2,
  triggerMode: 'ALL_PAID' as string,
  eligibleOrderTypes: ['NEW', 'RENEW', 'UPGRADE'] as string[],
  isActive: true,
  effectiveAt: '',
  levels: [
    { level: 1, ratePercent: 15 },
    { level: 2, ratePercent: 5 },
  ] as LevelRow[],
})

/** 编辑模式时回填表单 */
watch(() => props.policy, (p) => {
  if (p) {
    form.name = p.name
    form.segment = p.segment
    form.maxLevel = p.maxLevel
    form.triggerMode = p.triggerMode
    form.eligibleOrderTypes = [...p.eligibleOrderTypes]
    form.isActive = p.isActive
    form.effectiveAt = p.effectiveAt ? p.effectiveAt.slice(0, 16) : ''
    form.levels = p.levels.map(lv => ({
      level: lv.level,
      ratePercent: Math.round(lv.rate * 100 * 10) / 10,
    }))
  }
}, { immediate: true })

const addLevel = () => {
  const nextLevel = form.levels.length > 0
    ? Math.max(...form.levels.map(l => l.level)) + 1
    : 1
  form.levels.push({ level: nextLevel, ratePercent: 5 })
}

const removeLevel = (idx: number) => {
  form.levels.splice(idx, 1)
}

const handleSubmit = () => {
  const data: PolicyCreateRequest = {
    name: form.name,
    segment: form.segment,
    maxLevel: form.maxLevel,
    triggerMode: form.triggerMode as any,
    eligibleOrderTypes: form.eligibleOrderTypes,
    isActive: form.isActive,
    effectiveAt: form.effectiveAt || null,
    levels: form.levels.map(lv => ({
      level: lv.level,
      rate: lv.ratePercent / 100,
    })),
  }
  emit('submit', data)
}
</script>
